/** Rubrique Cuvée-Bouteille — Choix par défauts. Enregistrement automatique (useFormAutoSave). */
import { useState, useEffect, useMemo } from 'react';
import { metaApi } from '../../../services/api';
import { Spinner, Card } from '../../../components/ui/kit';
import { BOTTLE_FORMAT_OPTIONS } from '../../../constants';
import { useFormAutoSave, AutoSaveButton } from '../../../hooks/useFormAutoSave';

const COLORS = [['rouge', 'Rouge'], ['blanc', 'Blanc'], ['rose', 'Rosé'], ['orange', 'Orange'], ['gris', 'Gris'], ['vert', 'Vert']];
const TYPES = [['tranquille', 'Tranquille'], ['effervescent', 'Effervescent'], ['doux', 'Doux / liquoreux'], ['vdn', 'Vin doux naturel']];

export default function DefautsBouteille() {
  const [loaded, setLoaded] = useState(null);
  const [est, setEst] = useState(null);
  useEffect(() => { metaApi.settings().then((e) => { setLoaded(e); setEst(e); }); }, []);

  // Valeurs suivies par l'enregistrement automatique.
  const values = useMemo(() => est && ({
    default_by_glass: !!est.default_by_glass,
    default_on_wine_list: est.default_on_wine_list !== false,
    default_format: est.default_format || '750',
    default_alert_threshold: Number(est.default_alert_threshold || 6),
    default_color: est.default_color || 'rouge',
    default_type: est.default_type || 'tranquille',
  }), [est]);

  const initial = useMemo(() => loaded && ({
    default_by_glass: !!loaded.default_by_glass,
    default_on_wine_list: loaded.default_on_wine_list !== false,
    default_format: loaded.default_format || '750',
    default_alert_threshold: Number(loaded.default_alert_threshold || 6),
    default_color: loaded.default_color || 'rouge',
    default_type: loaded.default_type || 'tranquille',
  }), [loaded]);

  const { status, pending, saveNow, errors } = useFormAutoSave('/parametres/establishment', values, { initial });

  if (!est) return <Spinner />;
  const set = (k, v) => setEst({ ...est, [k]: v });

  return (
    <div className="max-w-[640px]">
      <h2 className="font-title text-3xl mb-1">Cuvée-Bouteille — Choix par défauts</h2>
      <p className="text-[13px] text-n600 mb-5">Valeurs proposées automatiquement à la création d'une cuvée et lors d'une entrée en stock. Enregistrement automatique.</p>
      <Card className="p-5 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer" data-testid="def-by-glass">
          <input type="checkbox" className="accent-[#b68235] w-4 h-4" checked={!!est.default_by_glass} onChange={(e) => set('default_by_glass', e.target.checked)} />
          <span><span className="text-[14px]">Proposer au verre par défaut</span><span className="block text-[12px] text-n500">La case « au verre » sera pré-cochée.</span></span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="def-on-wine-list">
          <input type="checkbox" className="accent-[#b68235] w-4 h-4" checked={est.default_on_wine_list !== false} onChange={(e) => set('default_on_wine_list', e.target.checked)} />
          <span><span className="text-[14px]">Proposer à la carte (à la bouteille) par défaut</span><span className="block text-[12px] text-n500">La case « à la carte » sera pré-cochée.</span></span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Format par défaut">
            <select className="vd-input h-9 w-full" value={est.default_format || '750'} onChange={(e) => set('default_format', e.target.value)} data-testid="def-format">
              {BOTTLE_FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Seuil d'alerte stock par défaut" error={errors.default_alert_threshold}>
            <input type="number" min="0" className="vd-input h-9 w-full nums" value={est.default_alert_threshold ?? 6} onChange={(e) => set('default_alert_threshold', e.target.value)} data-testid="def-threshold" />
          </Field>
          <Field label="Couleur par défaut">
            <select className="vd-input h-9 w-full" value={est.default_color || 'rouge'} onChange={(e) => set('default_color', e.target.value)} data-testid="def-color">
              {COLORS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Type par défaut">
            <select className="vd-input h-9 w-full" value={est.default_type || 'tranquille'} onChange={(e) => set('default_type', e.target.value)} data-testid="def-type">
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
        {errors._global && <p className="text-[12px] text-red-600">{errors._global}</p>}
        <AutoSaveButton status={status} pending={pending} saveNow={saveNow} />
      </Card>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="kicker block mb-1">{label}</span>
      {children}
      {error && <span className="block text-[11px] text-red-600 mt-0.5">{error}</span>}
    </label>
  );
}
